import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaPlus, FaSearch, FaPrint, FaCheck, FaTrash, FaVial, FaQrcode } from 'react-icons/fa';
import api from '../api';
import { theme, t } from '../theme';
import PageLayout from './PageLayout';
import EtiquetaModal from './EtiquetaModal';
import BatchReport from './BatchReport';
import { imprimirEtiquetas, pdfDeTeste } from '../platform/etiqueta';
import { codigoDeQr } from '../platform/enderecoWeb';
import { disponivel as scannerDisponivel, escanear } from '../platform/scanner';
import { entregarPdf } from '../platform/arquivos';
import {
  aparenciaDaSituacao,
  copiasParaFornada,
  dataCurta,
  ordenarPorUrgencia,
  ROTULO_CONSERVACAO,
  textoDoSaldo,
} from './etiquetaValidade';

/**
 * Etiquetas da cozinha — o que está aberto ou pronto, e quando vence.
 *
 * NÃO É A MESMA COISA QUE "Controle de Lotes" (/lotes). Lá é o que foi
 * COMPRADO e ainda está fechado, com a validade do fabricante. Aqui é o que
 * foi ABERTO ou PRODUZIDO, com a validade da manipulação — quase sempre bem
 * menor. Um saco de chocolate lacrado é um lote; virado em três embalagens de
 * ganache, são três etiquetas.
 *
 * A ORDEM É POR URGÊNCIA, não por data de criação: quem abre esta tela quer
 * saber o que jogar fora hoje, não o que fez ontem.
 */
const FILTROS = [
  { id: 'todas', rotulo: 'Todas' },
  { id: 'vencida', rotulo: 'Vencidas' },
  { id: 'hoje', rotulo: 'Vencem hoje' },
  { id: 'amanha', rotulo: 'Vencem amanhã' },
];

const MOTIVOS = [
  { id: 'venceu', rotulo: 'Venceu' },
  { id: 'sobra', rotulo: 'Sobra' },
  { id: 'caiu', rotulo: 'Caiu / estragou' },
  { id: 'erro', rotulo: 'Etiqueta errada' },
];

const Etiquetas = () => {
  const [etiquetas, setEtiquetas] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);
  const [modalAberto, setModalAberto] = useState(false);
  const [descartando, setDescartando] = useState(null);
  const navigate = useNavigate();
  // A aba vive na URL para o menu poder apontar direto para o estoque e para
  // um link compartilhado abrir onde a pessoa estava.
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') === 'estoque' ? 'estoque' : 'cozinha';

  const carregar = useCallback(async () => {
    // Na aba de estoque quem manda e o BatchReport, com a propria chamada.
    // Buscar etiquetas aqui seria trabalho jogado fora a cada troca de aba.
    if (aba === 'estoque') { setCarregando(false); return; }
    setCarregando(true);
    try {
      const params = {};
      if (filtro !== 'todas') params.situacao = filtro;
      if (busca.trim()) params.busca = busca.trim();
      const [lista, contagem] = await Promise.all([
        api.get('/api/etiquetas', { params }),
        api.get('/api/etiquetas/resumo'),
      ]);
      setEtiquetas(ordenarPorUrgencia(lista.data));
      setResumo(contagem.data);
      setErro(null);
    } catch {
      setErro('Não consegui carregar as etiquetas.');
    } finally {
      setCarregando(false);
    }
  }, [filtro, busca, aba]);

  useEffect(() => {
    const id = setTimeout(carregar, busca ? 300 : 0);
    return () => clearTimeout(id);
  }, [carregar, busca]);

  const reimprimir = async (etiqueta) => {
    try {
      // Conta a reimpressão antes de imprimir: etiqueta molha, descola e some,
      // e saber quantas vezes algo foi reetiquetado é informação real.
      await api.post(`/api/etiquetas/${etiqueta.codigo}/reimprimir`);
      await imprimirEtiquetas(copiasParaFornada(etiqueta), `etiqueta-${etiqueta.codigo}.pdf`);
    } catch {
      setErro('Não consegui reimprimir.');
    }
  };

  const consumir = async (etiqueta) => {
    try {
      await api.post(`/api/etiquetas/${etiqueta.codigo}/consumir`, { unidades: 1 });
      carregar();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não consegui dar baixa.');
    }
  };

  const descartar = async (etiqueta, motivo) => {
    try {
      await api.post(`/api/etiquetas/${etiqueta.codigo}/descartar`, { motivo });
      setDescartando(null);
      carregar();
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não consegui descartar.');
    }
  };

  /**
   * Le o QR e abre a etiqueta.
   *
   * So aparece no app: na web `scannerDisponivel()` e sempre false, e la o
   * caminho natural e a propria camera do celular abrindo a URL do QR.
   */
  const lerEtiqueta = async () => {
    const r = await escanear({ tipo: 'qr' });
    if (!r.ok) {
      if (r.motivo !== 'cancelado') setErro('Nao consegui abrir a camera.');
      return;
    }
    const codigo = codigoDeQr(r.valor);
    if (!codigo) {
      // Ler o QR de outra coisa e o erro mais comum aqui, e "codigo invalido"
      // nao ajudaria ninguem a entender o que fazer.
      setErro('Esse codigo nao e de uma etiqueta do Metria.');
      return;
    }
    navigate(`/e/${codigo}`);
  };

  const imprimirTeste = async () => {
    // Antes da primeira etiqueta de verdade: se a moldura sai inteira, o driver
    // está com o papel certo. É o que separa "instalei" de um chamado por semana.
    const doc = await pdfDeTeste();
    await entregarPdf(doc, 'teste-de-impressao.pdf');
  };

  return (
    <PageLayout
      title="Validade"
      subtitle="O que vence primeiro, aberto ou no estoque"
      action={(
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {aba === 'estoque' ? null : (
          <>
          {scannerDisponivel() && (
            <button onClick={lerEtiqueta} style={t.btnSecondary}>
              <FaQrcode /> Ler etiqueta
            </button>
          )}
          <button onClick={imprimirTeste} style={t.btnSecondary} title="Imprime uma moldura para conferir o papel">
            <FaVial /> Testar impressão
          </button>
          <button onClick={() => setModalAberto(true)} style={t.btnPrimary}>
            <FaPlus /> Nova etiqueta
          </button>
          </>
          )}
        </div>
      )}
    >
      {/* Duas geladeiras, uma pergunta. "Na cozinha" e o que foi ABERTO ou
          produzido, com a validade da manipulacao; "No estoque" e o que esta
          FECHADO, com a validade do fabricante. Sao dados diferentes que a
          confeiteira consulta pelo mesmo motivo — o que vou perder? */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${theme.colors.border}` }}>
        {[['cozinha', 'Na cozinha'], ['estoque', 'No estoque']].map(([id, rotulo]) => (
          <button key={id} onClick={() => setParams(id === 'cozinha' ? {} : { aba: id })}
            style={{
              background: 'none', border: 'none', cursor: 'pointer', padding: '10px 14px',
              fontSize: 14, fontWeight: aba === id ? 700 : 500,
              color: aba === id ? theme.colors.primary : theme.colors.textSecondary,
              borderBottom: `2px solid ${aba === id ? theme.colors.primary : 'transparent'}`,
              marginBottom: -1,
            }}>
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'estoque' ? (
        <BatchReport embutido />
      ) : (
      <>
      {resumo && resumo.alerta > 0 && (
        <div style={{
          background: '#fef5e7', border: '1px solid #f5cba7', borderRadius: 12,
          padding: '12px 14px', marginBottom: 16, fontSize: 14, color: '#7e5109',
        }}>
          <strong>{resumo.alerta}</strong>
          {resumo.alerta === 1 ? ' etiqueta precisa' : ' etiquetas precisam'} de atenção
          {resumo.vencida > 0 && ` · ${resumo.vencida} vencida${resumo.vencida > 1 ? 's' : ''}`}
          {resumo.hoje > 0 && ` · ${resumo.hoje} vence${resumo.hoje > 1 ? 'm' : ''} hoje`}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
          <FaSearch style={{ position: 'absolute', left: 12, top: 11, color: '#aaa' }} />
          <input placeholder="Buscar pelo nome..." value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ ...t.input, paddingLeft: 36 }} />
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {FILTROS.map((f) => (
            <button key={f.id} onClick={() => setFiltro(f.id)}
              style={{
                ...t.btnSecondary,
                background: filtro === f.id ? theme.colors.primary : '#fff',
                color: filtro === f.id ? '#fff' : theme.colors.textSecondary,
                borderColor: filtro === f.id ? theme.colors.primary : theme.colors.border,
                whiteSpace: 'nowrap',
              }}>
              {f.rotulo}
              {f.id !== 'todas' && resumo?.[f.id] > 0 && ` (${resumo[f.id]})`}
            </button>
          ))}
        </div>
      </div>

      {erro && (
        <div style={{ background: '#fdecea', color: '#c0392b', padding: 12, borderRadius: 10, marginBottom: 12, fontSize: 13 }}>
          {erro}
        </div>
      )}

      {carregando ? (
        <div style={{ textAlign: 'center', padding: 60, color: theme.colors.textMuted }}>Carregando...</div>
      ) : etiquetas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: theme.colors.textMuted }}>
          <p style={{ fontWeight: 600, marginBottom: 4 }}>
            {filtro === 'todas' ? 'Nenhuma etiqueta ainda.' : 'Nada nesse filtro.'}
          </p>
          {filtro === 'todas' && (
            <p style={{ fontSize: 13 }}>
              Imprima uma ao guardar a próxima embalagem — é assim que o painel de validade se enche.
            </p>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {etiquetas.map((e) => {
            const ap = aparenciaDaSituacao(e.situacao);
            const saldo = textoDoSaldo(e);
            return (
              <div key={e.codigo} style={{
                background: '#fff', border: `1px solid ${theme.colors.border}`,
                borderLeft: `4px solid ${ap.cor}`, borderRadius: 12, padding: 12,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, color: theme.colors.text }}>{e.nome}</div>
                    <div style={{ fontSize: 12, color: theme.colors.textMuted, marginTop: 2 }}>
                      {ROTULO_CONSERVACAO[e.conservacao] || e.conservacao}
                      {e.quantidade != null && ` · ${String(e.quantidade).replace('.', ',')}${e.unidade_medida || ''}`}
                      {saldo && ` · ${saldo}`}
                      {e.lote_texto && ` · lote ${e.lote_texto}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <span style={{
                      display: 'inline-block', background: ap.fundo, color: ap.cor,
                      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    }}>
                      {ap.rotulo}
                    </span>
                    <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 4 }}>
                      {dataCurta(e.validade)}
                    </div>
                  </div>
                </div>

                {descartando === e.codigo ? (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${theme.colors.borderLight || '#f0f0f0'}` }}>
                    <div style={{ fontSize: 12, color: theme.colors.textSecondary, marginBottom: 6 }}>
                      Por quê? <span style={{ color: '#aaa' }}>(vira relatório de perdas)</span>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {MOTIVOS.map((m) => (
                        <button key={m.id} onClick={() => descartar(e, m.id)}
                          style={{ ...t.btnSecondary, fontSize: 12, padding: '6px 10px' }}>
                          {m.rotulo}
                        </button>
                      ))}
                      <button onClick={() => setDescartando(null)}
                        style={{ ...t.btnSecondary, fontSize: 12, padding: '6px 10px', color: '#aaa' }}>
                        cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
                    <button onClick={() => reimprimir(e)} title="Reimprimir"
                      style={{ ...t.btnSecondary, padding: '6px 10px', fontSize: 12 }}>
                      <FaPrint /> Reimprimir
                    </button>
                    <button onClick={() => consumir(e)} title="Dar baixa de uma unidade"
                      style={{ ...t.btnSecondary, padding: '6px 10px', fontSize: 12, color: '#1e8449', borderColor: '#a9dfbf' }}>
                      <FaCheck /> {saldo ? 'Usei 1' : 'Acabou'}
                    </button>
                    <button onClick={() => setDescartando(e.codigo)} title="Descartar"
                      style={{ ...t.btnSecondary, padding: '6px 10px', fontSize: 12, color: theme.colors.danger, borderColor: '#f5b7b1' }}>
                      <FaTrash />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      </>
      )}

      {modalAberto && (
        <EtiquetaModal onFechar={() => setModalAberto(false)} onCriada={carregar} />
      )}
    </PageLayout>
  );
};

export default Etiquetas;
